import { type BlockStdScope, ConfigExtensionFactory } from '@blocksuite/std';
import type { GfxModel } from '@blocksuite/std/gfx';

export type CanvasContentConfig = {
  /**
   * When false, the canvas only holds the content its editor provides:
   * shortcuts that add notes, frames, groups, links or flow blocks are off,
   * so are cutting and pasting canvas content, and a connector must join two
   * elements — one left with a loose end is discarded.
   */
  freeContent: boolean;
  /**
   * The output a connector from `source` leaves from, read off the element
   * the drag started on (`null` when it started elsewhere): a port name,
   * `undefined` for a source without outputs of its own, `null` when no
   * connector may leave from there.
   */
  connectorSourcePort?: (
    source: GfxModel,
    origin: Element | null
  ) => string | null | undefined;
  /**
   * Delete and Backspace on a canvas without free content: the editor of the
   * canvas removes the selected elements by its own rules. Without it, the
   * keys delete nothing there.
   */
  deleteSelection?: (elementIds: readonly string[]) => void;
  /**
   * When false, the canvas draws no axes, rulers or world title; the area
   * outside the world and the fading grid edge stay.
   */
  worldMarkings?: boolean;
};

export const CanvasContentConfigExtension =
  ConfigExtensionFactory<CanvasContentConfig>('affine:canvas-content');

export function allowsFreeCanvasContent(std: BlockStdScope): boolean {
  return (
    std.getOptional(CanvasContentConfigExtension.identifier)?.freeContent ??
    true
  );
}

/** See `CanvasContentConfig.worldMarkings`. */
export function showsCanvasWorldMarkings(std: BlockStdScope): boolean {
  return (
    std.getOptional(CanvasContentConfigExtension.identifier)?.worldMarkings ??
    true
  );
}

/**
 * Hands the selected elements of a canvas without free content to its editor
 * (see `CanvasContentConfig.deleteSelection`).
 */
export function deleteCanvasSelection(
  std: BlockStdScope,
  elementIds: readonly string[]
): void {
  std
    .getOptional(CanvasContentConfigExtension.identifier)
    ?.deleteSelection?.(elementIds);
}

/** See `CanvasContentConfig.connectorSourcePort`; a canvas without it has no ports. */
export function connectorSourcePortAt(
  std: BlockStdScope,
  source: GfxModel,
  origin: Element | null
): string | null | undefined {
  return std
    .getOptional(CanvasContentConfigExtension.identifier)
    ?.connectorSourcePort?.(source, origin);
}
